// src/ui/screens/chats/widgets/PinnedMessagesBanner.jsx

import React, { useState, useEffect, useRef } from 'react';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
};

// ── PinnedMessageInfo class ────────────────────────────────────────────────

export class PinnedMessageInfo {
  constructor({ id, preview, type, attachmentUrl = null }) {
    this.id = id;
    this.preview = preview;
    this.type = type; // 'text' | 'image' | 'audio'
    this.attachmentUrl = attachmentUrl;
  }
}

// ── PinnedMessagesBanner ────────────────────────────────────────────────────

export const PinnedMessagesBanner = ({
  pinnedMessages = [],
  onTap,
  onUnpin,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  const timeoutRef = useRef(null);

  // ── Reset index when messages change ─────────────────────────────────────

  useEffect(() => {
    setCurrentIndex(Math.min(currentIndex, Math.max(0, pinnedMessages.length - 1)));
  }, [pinnedMessages.length]);

  // ── Cleanup timeout ──────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // ── Cycle pins ────────────────────────────────────────────────────────────

  const cyclePins = () => {
    if (pinnedMessages.length <= 1) return;

    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex((currentIndex + 1) % pinnedMessages.length);
      setIsFading(false);
    }, 150);
  };

  // ── Show unpin dialog ────────────────────────────────────────────────────

  const showUnpinDialog = (msg) => {
    if (window.confirm(`Unpin this message?`)) {
      onUnpin(msg.id);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (pinnedMessages.length === 0) return null;

  const msg = pinnedMessages[currentIndex];
  const total = pinnedMessages.length;

  return (
    <div
      className={`px-3.5 py-2 border-b border-white/10 flex items-center gap-2.5 transition-opacity duration-150 ${
        isFading ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: `${COLORS.bg}F5` }}
    >
      {/* Left indicator bars */}
      <div className="flex flex-col items-center gap-0.5">
        {total > 1 ? (
          pinnedMessages.map((_, i) => (
            <div
              key={i}
              className={`w-0.5 rounded-full transition-all duration-200 ${
                i === currentIndex
                  ? 'h-4.5 bg-filatom-primary'
                  : 'h-2 bg-white/25'
              }`}
            />
          ))
        ) : (
          <div className="w-0.5 h-7 rounded-full" style={{ backgroundColor: COLORS.orange }} />
        )}
      </div>

      {/* Thumbnail for image pins */}
      {msg.type === 'image' && msg.attachmentUrl && (
        <img
          src={msg.attachmentUrl}
          alt="Pinned image"
          className="w-8 h-8 rounded object-cover flex-shrink-0"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}

      {/* Content */}
      <button
        onClick={() => {
          onTap(msg.id);
          cyclePins();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          showUnpinDialog(msg);
        }}
        className="flex-1 text-left min-w-0"
      >
        <p className="text-filatom-primary text-[11px] font-semibold tracking-wide">
          {total > 1 ? `Pinned message ${currentIndex + 1} of ${total}` : 'Pinned message'}
        </p>
        <p className="text-white/75 text-xs truncate">{msg.preview}</p>
      </button>

      {/* Pin icon */}
      <svg className="w-4 h-4 text-filatom-primary flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
        <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" />
      </svg>
    </div>
  );
};

export default PinnedMessagesBanner;